import type { IconName } from '../../components/ui/Icon.js';

/**
 * Account configuration menu.
 *
 * Structured after the feature taxonomy a hosted-PBX customer expects — call
 * management, contact centre, media, numbers, reporting, billing — rather than
 * after our table layout. Customers look for "after-hours routing", not for the
 * table it happens to live in.
 *
 * `phase` marks sections whose backend does not exist yet. They are SHOWN
 * rather than hidden: the shape of the product is part of the sell, and an
 * honest "planned for Phase 2" beats a menu that quietly lacks half of what was
 * discussed. `permission` hides what this user may not touch.
 */
export interface ConfigItem {
  key: string;
  label: string;
  /** Set when there is no backend yet; renders a labelled placeholder. */
  phase?: string;
  permission?: string;
  /** Count shown as a pill, resolved by the page. */
  countKey?: 'extensions' | 'ringGroups' | 'queues' | 'ivrs' | 'numbers' | 'trunks' | 'users';
}

export interface ConfigGroup {
  key: string;
  label: string;
  icon: IconName;
  items: ConfigItem[];
}

export const CONFIG_NAV: ConfigGroup[] = [
  {
    key: 'overview', label: 'Overview', icon: 'dashboard',
    items: [
      { key: 'dashboard', label: 'Dashboard' },
    ],
  },
  {
    key: 'people', label: 'Users & devices', icon: 'users',
    items: [
      { key: 'extensions', label: 'Extensions', permission: 'extension:read', countKey: 'extensions' },
      { key: 'devices', label: 'Handsets', permission: 'device:read' },
      { key: 'hotdesk', label: 'Hot desking', phase: 'Phase 3', permission: 'device:read' },
      { key: 'portal-users', label: 'Portal users', permission: 'user:read', countKey: 'users' },
    ],
  },
  {
    key: 'routing', label: 'Call routing', icon: 'ivr',
    items: [
      { key: 'inbound', label: 'Inbound routes', permission: 'number:read', countKey: 'numbers' },
      { key: 'ivrs', label: 'IVR menus', permission: 'flow:read', countKey: 'ivrs' },
      { key: 'ring-groups', label: 'Hunt lists', permission: 'extension:read', countKey: 'ringGroups' },
      { key: 'queues', label: 'Queues', permission: 'queue:read', countKey: 'queues' },
      { key: 'schedules', label: 'Time schedules', permission: 'extension:read' },
      { key: 'holidays', label: 'Public holidays', phase: 'Phase 2', permission: 'extension:read' },
      { key: 'outbound', label: 'Outbound routes', phase: 'Phase 2', permission: 'outboundroute:read' },
      { key: 'dial-permissions', label: 'Dialling restrictions', permission: 'extension:read' },
    ],
  },
  {
    key: 'features', label: 'Voice features', icon: 'phone',
    items: [
      { key: 'voicemail', label: 'Voicemail', permission: 'voicemail:read' },
      { key: 'recording', label: 'Call recording', permission: 'recording:read' },
      { key: 'conference', label: 'Conference rooms', phase: 'Phase 3' },
      { key: 'forwarding', label: 'Forwarding & follow-me', permission: 'extension:read' },
      { key: 'screening', label: 'Call screening & blocking', phase: 'Phase 3' },
      { key: 'feature-codes', label: 'Feature codes', phase: 'Phase 2', permission: 'featurecode:write' },
    ],
  },
  {
    key: 'contact-centre', label: 'Contact centre', icon: 'queue',
    items: [
      { key: 'agents', label: 'Agents & skills', phase: 'Phase 3', permission: 'queue:read' },
      { key: 'wallboard', label: 'Live wallboard', phase: 'Phase 3', permission: 'queue:read' },
      { key: 'campaigns', label: 'Dialler campaigns', phase: 'Phase 4', permission: 'queue:read' },
      { key: 'dispositions', label: 'Dispositions', phase: 'Phase 4', permission: 'queue:read' },
      { key: 'qa', label: 'Agent scoring', phase: 'Phase 4', permission: 'queue:read' },
    ],
  },
  {
    key: 'media', label: 'Media', icon: 'media',
    items: [
      { key: 'prompts', label: 'Prompts & greetings', phase: 'Phase 2' },
      { key: 'moh', label: 'Music on hold', phase: 'Phase 2' },
    ],
  },
  {
    key: 'numbers', label: 'Numbers', icon: 'hash',
    items: [
      { key: 'numbers', label: 'Assigned numbers', permission: 'number:read', countKey: 'numbers' },
      { key: 'porting', label: 'Porting', phase: 'Phase 2', permission: 'port:read' },
    ],
  },
  {
    key: 'connectivity', label: 'Connectivity', icon: 'carrier',
    items: [
      { key: 'trunks', label: 'Carrier trunks', permission: 'trunk:read', countKey: 'trunks' },
      { key: 'codecs', label: 'Codecs & media', phase: 'Phase 2', permission: 'trunk:read' },
    ],
  },
  {
    key: 'reporting', label: 'Reporting', icon: 'rates',
    items: [
      { key: 'calls', label: 'Call history', permission: 'cdr:read' },
      { key: 'recordings', label: 'Recordings', phase: 'Phase 2', permission: 'recording:read' },
      { key: 'analytics', label: 'Call analytics', phase: 'Phase 3', permission: 'report:read' },
      { key: 'queue-reports', label: 'Queue performance', phase: 'Phase 3', permission: 'report:read' },
      { key: 'agent-reports', label: 'Agent activity', phase: 'Phase 3', permission: 'report:read' },
    ],
  },
  {
    key: 'billing', label: 'Billing', icon: 'billing',
    items: [
      { key: 'billing', label: 'Balance & ledger', permission: 'billing:read' },
      { key: 'invoices', label: 'Invoices', phase: 'Phase 1J', permission: 'invoice:read' },
      { key: 'usage', label: 'Usage breakdown', phase: 'Phase 1J', permission: 'billing:read' },
      { key: 'spend-controls', label: 'Spend controls', phase: 'Phase 1J', permission: 'creditlimit:write' },
    ],
  },
  {
    key: 'settings', label: 'Settings', icon: 'settings',
    items: [
      { key: 'profile', label: 'Company profile', permission: 'tenant:read' },
      { key: 'hours', label: 'Business hours', phase: 'Phase 2', permission: 'tenant:read' },
      { key: 'integrations', label: 'Integrations & webhooks', phase: 'Phase 4', permission: 'webhook:read' },
      { key: 'api-keys', label: 'API keys', phase: 'Phase 4', permission: 'apikey:read' },
      { key: 'audit', label: 'Audit log', permission: 'audit:read' },
    ],
  },
];

/** Which group a section belongs to, so the right group opens on load. */
export function groupFor(itemKey: string): string | null {
  for (const g of CONFIG_NAV) {
    if (g.items.some((i) => i.key === itemKey)) return g.key;
  }
  return null;
}
